import { useState } from "react";
import { useVehicle } from "../context/VehicleContext";
import { useMeasurements } from "../context/MeasurementsContext";
import { evaluate, STATUS_META } from "../lib/specs";
import { saveReport } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { FileText, Camera, Check, Share2, Copy, ExternalLink, Trash2 } from "lucide-react";
import { toast } from "sonner";

const snapshot = (m, specs) => {
  const ev = evaluate(m, specs);
  return {
    ts: new Date().toISOString(),
    passRate: ev.passRate,
    rows: ev.checks.map((c) => ({
      label: c.label, value: c.value ?? "", status: c.status,
      target: c.spec.target, min: c.spec.min, max: c.spec.max,
    })),
  };
};

const Snap = ({ snap, title, testId }) => (
  <div className="rounded-md bg-surface border border-steel p-5" data-testid={testId}>
    <div className="flex items-center justify-between mb-3">
      <span className="font-mono text-[11px] text-slate-400 tracking-widest uppercase">{title}</span>
      {snap && <span className="font-mono text-sm font-bold" style={{ color: snap.passRate >= 80 ? "#10B981" : snap.passRate >= 50 ? "#F59E0B" : "#EF4444" }}>{snap.passRate}% in spec</span>}
    </div>
    {!snap ? (
      <p className="font-mono text-[11px] text-slate-600">Not captured yet.</p>
    ) : (
      <div className="space-y-1">
        {snap.rows.filter((x) => x.status !== "empty").map((x, i) => {
          const meta = STATUS_META[x.status];
          return (
            <div key={i} className="flex items-center justify-between text-xs">
              <span className="text-slate-400">{x.label}</span>
              <span className="flex items-center gap-2">
                <span className="font-mono tabular-nums text-white">{x.value}°</span>
                <span className="font-mono text-[9px] px-1.5 py-0.5 rounded" style={{ color: meta.color, background: meta.bg }}>{meta.label}</span>
              </span>
            </div>
          );
        })}
      </div>
    )}
  </div>
);

export default function ReportBuilder() {
  const { specs, vehicleLabel } = useVehicle();
  const { m } = useMeasurements();
  const [before, setBefore] = useState(null);
  const [after, setAfter] = useState(null);
  const [techName, setTechName] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [link, setLink] = useState(null);

  const capture = (which) => {
    const s = snapshot(m, specs);
    if (which === "before") setBefore(s); else setAfter(s);
    toast.success(`${which === "before" ? "Before" : "After"} captured — ${s.passRate}% in spec`);
    setLink(null);
  };

  const generate = async () => {
    if (!before && !after) { toast.error("Capture a Before and/or After first"); return; }
    setSaving(true);
    try {
      const rep = await saveReport({ vehicleLabel, techName: techName || null, notes: notes || null, before, after });
      const url = `${window.location.origin}/report/${rep.id}`;
      setLink(url);
      toast.success("Shareable report created");
    } catch { toast.error("Could not create report — try again"); }
    finally { setSaving(false); }
  };

  const copy = () => { navigator.clipboard.writeText(link); toast.success("Link copied"); };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="report-builder-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-11 h-11 rounded-lg bg-cyan/10 border border-cyan/20 flex items-center justify-center text-cyan"><FileText size={22} /></div>
        <div>
          <h1 className="font-heading font-bold tracking-tight text-2xl">Before / After Report</h1>
          <p className="font-mono text-[11px] text-slate-500 tracking-widest uppercase">{vehicleLabel || "No vehicle"} · shareable alignment record</p>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <Snap snap={before} title="Before" testId="report-before" />
        <Snap snap={after} title="After" testId="report-after" />
      </div>

      <div className="mt-4 grid grid-cols-2 gap-2">
        <Button data-testid="report-capture-before" onClick={() => capture("before")} className="bg-elevated border border-steel text-slate-200 hover:bg-steel font-mono text-xs uppercase tracking-widest">
          <Camera size={14} /> Capture Before
        </Button>
        <Button data-testid="report-capture-after" onClick={() => capture("after")} className="bg-elevated border border-steel text-slate-200 hover:bg-steel font-mono text-xs uppercase tracking-widest">
          <Camera size={14} /> Capture After
        </Button>
      </div>

      <div className="mt-6 rounded-md bg-surface border border-steel p-5 space-y-4">
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="font-mono text-[10px] text-slate-400 tracking-widest uppercase">Technician / shop</label>
            <Input data-testid="report-tech" value={techName} onChange={(e) => setTechName(e.target.value)} placeholder="Optional" className="mt-1 bg-ink border-steel text-white" />
          </div>
          <div>
            <label className="font-mono text-[10px] text-slate-400 tracking-widest uppercase">Notes</label>
            <Textarea data-testid="report-notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional notes for the customer" className="mt-1 bg-ink border-steel text-white min-h-[42px]" />
          </div>
        </div>

        {!link ? (
          <Button data-testid="report-generate" onClick={generate} disabled={saving} className="w-full bg-cyan text-ink hover:bg-cyan-hover font-mono text-xs uppercase tracking-widest font-bold disabled:opacity-40">
            <Share2 size={15} /> {saving ? "Creating…" : "Generate Shareable Report"}
          </Button>
        ) : (
          <div className="rounded-md bg-pass/5 border border-pass/30 p-4" data-testid="report-link-box">
            <div className="font-mono text-[10px] text-pass tracking-widest uppercase mb-2 flex items-center gap-1"><Check size={13} /> Report ready — share this link</div>
            <div className="flex items-center gap-2 bg-ink border border-steel rounded-lg px-3 py-2.5">
              <code className="font-mono text-xs text-cyan flex-1 truncate" data-testid="report-link">{link}</code>
              <button onClick={copy} data-testid="report-copy" className="text-slate-400 hover:text-cyan"><Copy size={15} /></button>
              <a href={link} target="_blank" rel="noreferrer" data-testid="report-open" className="text-slate-400 hover:text-cyan"><ExternalLink size={15} /></a>
            </div>
            <Button data-testid="report-new" onClick={() => { setLink(null); setBefore(null); setAfter(null); setNotes(""); }} variant="outline" className="mt-3 bg-elevated border-steel text-slate-300 hover:bg-steel font-mono text-[11px] uppercase tracking-widest">
              <Trash2 size={13} /> Start new report
            </Button>
          </div>
        )}
      </div>

      <p className="mt-4 font-mono text-[10px] text-slate-600 leading-relaxed">
        Capture your current Quick Align measurements as "Before", make adjustments, then capture "After". The shareable link opens a clean, printable report anyone can view — perfect for handing to a customer.
      </p>
    </div>
  );
}
